// Test PDF Scanner Page
// Simple test page to demonstrate PDF scanning functionality

import React, { useState, useEffect } from 'react';

const TestPDFScanner = () => {
  const [pdfjsLib, setPdfjsLib] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  // Load PDF.js library
  useEffect(() => {
    const loadPDFJS = async () => {
      try {
        const pdfjs = await import('pdfjs-dist');
        pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
        setPdfjsLib(pdfjs);
      } catch (err) {
        setError('Failed to load PDF.js library');
      }
    };

    loadPDFJS();
  }, []);

  // Test scan with the existing JEE Mains PDF
  const testScan = async () => {
    if (!pdfjsLib) {
      setError('PDF.js library not loaded');
      return;
    }

    setScanning(true);
    setError(null);
    setResults(null);

    try {
      
      // Scanner functionality removed - file not found
      setError('PDF Scanner functionality is currently unavailable');
      
      // Fetch the PDF file
      const response = await fetch('/jee-mains-2025.pdf');
      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.status}`);
      }
      
      const arrayBuffer = await response.arrayBuffer();
      
      // Create mock file object
      const mockFile = {
        arrayBuffer: () => Promise.resolve(arrayBuffer),
        name: 'JEE Main 2025 (24 Jan Shift 2) Previous Year Paper with Answer Keys - MathonGo.pdf',
        type: 'application/pdf'
      };
      
      // Scanner functionality removed - file not found
      setError('PDF Scanner functionality is currently unavailable');
      
    } catch (err) {
      setError(`Scan failed: ${err.message}`);
    } finally {
      setScanning(false);
    }
  };

  // Export results
  const exportResults = (format) => {
    if (!results) return;
    
    try {
      // Scanner functionality removed - file not found
      setError('Export functionality is currently unavailable');
    } catch (err) {
      setError(`Export failed: ${err.message}`);
    }
  };


  // Quick test scan
  const handleQuickTest = async () => {
    if (!pdfjsLib) {
      setError('PDF.js library not loaded');
      return;
    }

    setScanning(true);
    setError(null);

    try {
      
      // Scanner functionality removed - file not found
      setError('Quick test functionality is currently unavailable');
      
      // Fetch the PDF file
      const response = await fetch('/jee-mains-2025.pdf');
      const arrayBuffer = await response.arrayBuffer();
      
      // Create mock file
      const mockFile = {
        arrayBuffer: () => Promise.resolve(arrayBuffer),
        name: 'jee-mains-2025.pdf',
        type: 'application/pdf'
      };
      
      // Load PDF
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      
      // Extract text from first page only
      const page = await pdf.getPage(1);
      const content = await page.getTextContent();
      const pageText = content.items
        .map(item => {
          if (item && typeof item.str === 'string') {
            return item.str;
          }
          return '';
        })
        .filter(text => text && text.trim().length > 0)
        .join('\n');
      
      
      // Scanner functionality removed - file not found
      
      // Scanner functionality removed - file not found
      setError('Quick test functionality is currently unavailable');
      
    } catch (err) {
      setError(`Quick test failed: ${err.message}`);
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">
        JEE Mains PDF Scanner Test
      </h1>
      
      <div className="mb-6">
        <p className="text-gray-600 mb-4">
          This page tests the PDF scanning functionality with the existing JEE Mains PDF file.
        </p>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <h3 className="font-semibold text-blue-800 mb-2">Test PDF File</h3>
          <p className="text-blue-700">
            <strong>File:</strong> jee-mains-2025.pdf (JEE Main 2025 - 24 Jan Shift 2)
          </p>
          <p className="text-blue-700">
            <strong>Size:</strong> 1.74 MB
          </p>
          <p className="text-blue-700">
            <strong>Status:</strong> Ready for scanning
          </p>
        </div>
      </div>

      {/* Status */}
      <div className="mb-6">
        <div className="flex items-center space-x-4">
          <div className={`w-3 h-3 rounded-full ${pdfjsLib ? 'bg-green-500' : 'bg-red-500'}`}></div>
          <span className="text-sm text-gray-600">
            PDF.js Library: {pdfjsLib ? 'Loaded' : 'Loading...'}
          </span>
        </div>
      </div>

      {/* Test Buttons */}
      <div className="mb-6 flex space-x-4">
        <button
          onClick={testScan}
          disabled={scanning || !pdfjsLib}
          className={`px-6 py-3 rounded-lg font-semibold ${
            scanning || !pdfjsLib
              ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {scanning ? 'Scanning PDF...' : 'Test Scan PDF'}
        </button>
        
        
        <button
          onClick={handleQuickTest}
          disabled={scanning || !pdfjsLib}
          className={`px-6 py-3 rounded-lg font-semibold ${
            scanning || !pdfjsLib
              ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
              : 'bg-purple-600 text-white hover:bg-purple-700'
          }`}
        >
          {scanning ? 'Testing...' : 'Quick Test (1 Page)'}
        </button>
      </div>

      {/* Loading State */}
      {scanning && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            Analyzing PDF content...
          </p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <div className="text-red-400 mr-3">⚠️</div>
            <div>
              <h4 className="text-red-800 font-semibold">Error</h4>
              <p className="text-red-600">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      {results && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-green-800 mb-4">Scan Results</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg p-4">
                <div className="text-2xl font-bold text-green-600">
                  {results.totalQuestions}
                </div>
                <div className="text-sm text-gray-600">Questions Found</div>
              </div>
              <div className="bg-white rounded-lg p-4">
                <div className="text-2xl font-bold text-blue-600">
                  {results.totalPages}
                </div>
                <div className="text-sm text-gray-600">Pages Scanned</div>
              </div>
              <div className="bg-white rounded-lg p-4">
                <div className="text-2xl font-bold text-purple-600">
                  {results.subjects.length}
                </div>
                <div className="text-sm text-gray-600">Subjects</div>
              </div>
              <div className="bg-white rounded-lg p-4">
                <div className="text-2xl font-bold text-orange-600">
                  {results.difficulty}
                </div>
                <div className="text-sm text-gray-600">Difficulty</div>
              </div>
            </div>
            
            <div className="mt-4">
              <h4 className="font-semibold text-green-800 mb-2">Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-green-700">
                <div>
                  <p><strong>Year:</strong> {results.year || 'Unknown'}</p>
                  <p><strong>Shift:</strong> {results.shift || 'Unknown'}</p>
                  <p><strong>Subjects:</strong> {results.subjects.join(', ')}</p>
                </div>
                <div>
                  <p><strong>Method:</strong> {results.metadata.extractionMethod}</p>
                  <p><strong>Confidence:</strong> {Math.round(results.metadata.confidence * 100)}%</p>
                  <p><strong>Text Length:</strong> {results.metadata.textLength.toLocaleString()} chars</p>
                </div>
              </div>
            </div>
          </div>

          {/* Export Options */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Export Results</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => exportResults('json')}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Export JSON
              </button>
              <button
                onClick={() => exportResults('csv')}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Export CSV
              </button>
              <button
                onClick={() => exportResults('markdown')}
                className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
              >
                Export Markdown
              </button>
            </div>
          </div>

          {/* Sample Questions */}
          <div className="bg-white border border-gray-200 rounded-lg">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">
                Sample Questions ({Math.min(5, results.questions.length)} of {results.questions.length})
              </h3>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {results.questions.slice(0, 5).map((question, index) => (
                <div key={question.id || index} className="p-4 border-b border-gray-100 last:border-b-0">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium">
                        Q{question.number}
                      </span>
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm">
                        {question.subject}
                      </span>
                      <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-sm">
                        {question.difficulty}
                      </span>
                    </div>
                    <span className="text-sm text-gray-500">
                      {question.topic}
                    </span>
                  </div>
                  
                  <div className="mb-3">
                    <p className="text-gray-800 font-medium">
                      {question.question || 'Question text not available'}
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                    {question.options && question.options.length > 0 ? (
                      question.options.map((option, optIndex) => (
                        <div key={optIndex} className="flex items-center">
                          <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-sm mr-2">
                            {optIndex + 1}
                          </span>
                          <span className="text-sm text-gray-600">{option}</span>
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-gray-500 italic">No options found</div>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">Answer:</span>
                      <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-sm font-medium">
                        {question.correctAnswer || 'Not found'}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {question.tags.slice(0, 3).map((tag, tagIndex) => (
                        <span key={tagIndex} className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestPDFScanner;
